import { useEffect } from "react";
import { getTierCapabilities } from "@reach/core";
import type { SettingsAnalyticsGroup, SettingsFormGroup, SettingsKbGroup, SettingsUiGroup } from "./useSettingsPageState";

type UseSettingsPageEffectsParams = {
  analytics: Pick<SettingsAnalyticsGroup, "analyticsPeriod">;
  form: Pick<SettingsFormGroup, "density" | "formState">;
  kb: Pick<SettingsKbGroup, "kbItems" | "kbSearch" | "scrapeUrl" | "setKbPage" | "setShowSetupGuide">;
  ui: Pick<SettingsUiGroup, "selectedTab">;
  shopDomain: string;
  DENSITY_STORAGE_KEY: string;
  fetchAnalytics: () => void | Promise<void>;
  fetchConfig: () => void | Promise<void>;
  fetchGaps: () => void | Promise<void>;
  fetchKb: () => void | Promise<void>;
  fetchQueryLog: () => void | Promise<void>;
  normalizeScrapeUrl: (value: string) => void;
  showToast: (msg: string) => void;
};

export function useSettingsPageEffects({
  analytics,
  form,
  kb,
  ui,
  shopDomain,
  DENSITY_STORAGE_KEY,
  fetchAnalytics,
  fetchConfig,
  fetchGaps,
  fetchKb,
  fetchQueryLog,
  normalizeScrapeUrl,
  showToast,
}: UseSettingsPageEffectsParams) {
  // Mount-only: functions from createSettingsHandlers are recreated every render,
  // so listing them as deps would cause infinite request loops. Run once on mount.
  useEffect(() => {
    fetchConfig();
    fetchKb();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!getTierCapabilities(form.formState.planTier || "free").hasChatbotAnalytics) return;
    fetchGaps();
  }, [form.formState.planTier]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!getTierCapabilities(form.formState.planTier || "free").hasChatbotAnalytics) return;
    fetchQueryLog();
  }, [form.formState.planTier]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (kb.kbItems.length === 0) kb.setShowSetupGuide(true);
    else if (kb.kbItems.length > 5) kb.setShowSetupGuide(false);
  }, [kb.kbItems, kb.setShowSetupGuide]);

  // Re-run when period changes. Function ref is intentionally excluded (see above).
  useEffect(() => {
    fetchAnalytics();
    fetchQueryLog();
  }, [analytics.analyticsPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mount-only: check billing redirect params once on load.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing_success") === "1") {
      showToast("Subscription activated! Your plan has been updated.");
      fetchConfig();
      window.history.replaceState({}, "", `${window.location.pathname}?shop=${shopDomain}`);
    } else if (params.get("billing_cancelled") === "1") {
      showToast("Checkout cancelled. No changes were made.");
      window.history.replaceState({}, "", `${window.location.pathname}?shop=${shopDomain}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    kb.setKbPage(1);
  }, [kb.kbSearch, kb.setKbPage]);

  useEffect(() => {
    normalizeScrapeUrl(kb.scrapeUrl);
  }, [kb.scrapeUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      sessionStorage.setItem("reach_selected_tab", String(ui.selectedTab));
    } catch {}
  }, [ui.selectedTab]);

  useEffect(() => {
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, form.density);
    } catch {}
  }, [DENSITY_STORAGE_KEY, form.density]);
}
